import { Component, OnInit, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { FormsModule } from '@angular/forms';
import { MatChipsModule } from '@angular/material/chips';
import { PlacementService } from '../../core/services/placement-service/placement-service.service';
import { SharedModule } from '../../shared/shared.module';
import { ToastrService } from 'ngx-toastr';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MockTestCreationResponse } from '@edtech-ui/core/models/placement/placement.model';
import { EnrollCoursesDialogBoxComponent } from '@edtech-ui/student-dashboard-v2/enroll-courses-dialog-box/enroll-courses-dialog-box.component';
import { PermissionForStudents } from '@edtech-ui/access-role-permissions/users-permissions-constants/student-roles-permissions.enum';
import { HasPermissionDirective } from '@edtech-ui/access-role-permissions/users-permissions-directives/has-permissions.directives';

interface TestConfig {
  title: string;
  description: string;
  suggestedTopics: string[];
}

@Component({
  selector: 'app-placement-test-setup',
  standalone: true,
  imports: [CommonModule, RouterModule, MatIconModule, MatButtonModule, FormsModule, MatChipsModule, SharedModule, MatDialogModule,
	HasPermissionDirective
  ],
  templateUrl: './placement-test-setup.component.html',
  styleUrl: './placement-test-setup.component.scss'
})
export class PlacementTestSetupComponent implements OnInit {
  testType: string = '';
  config: TestConfig = { title: '', description: '', suggestedTopics: [] };

  selectedDifficulty: 'Easy' | 'Medium' | 'Hard' = 'Easy';
  selectedQuestions: number = 10;
  topicInput: string = '';
  selectedTopics: string[] = [];

  selectedDistribution: 'AI' | 'Equal' = 'AI';

  companies: string[] = [];
  roles: string[] = []; // Will be populated from API
  selectedCompany: string | null = null;
  selectedRole: string | null = null;
  companySearch: string = '';
  roleSearch: string = '';
  isCompanyDropdownOpen: boolean = false;
  isRoleDropdownOpen: boolean = false;
  private ignoreNextDocumentClick: boolean = false;

  uploadedFileName: string | null = null;
  uploadedFile: File | null = null; // Store file for API call
  extractedSkills: string[] = [];
  extractedExperiences: string[] = [];
  isExtracting: boolean = false;
  isGeneratingTest: boolean = false;
  PermissionForStudents = PermissionForStudents;

  private typeConfig: Record<string, TestConfig> = {
    'aptitude': {
      title: 'Aptitude Test Setup',
      description: 'Test your numerical reasoning, data interpretation, percentages, profit & loss, time & work, and mathematical problem-solving skills.',
      suggestedTopics: ['Percentages', 'Profit & Loss', 'Time & Work', 'Ratios', 'Speed & Distance', 'Simple Interest', 'Compound Interest', 'Averages']
    },
    'logical': {
      title: 'Logical Reasoning Test Setup',
      description: 'Practice puzzles, pattern recognition, seating arrangements, blood relations, coding-decoding, and logical deduction problems.',
      suggestedTopics: ['Puzzles', 'Pattern Recognition', 'Seating Arrangements', 'Blood Relations', 'Coding-Decoding', 'Syllogism']
    },
    'verbal': {
      title: 'Verbal Ability Test Setup',
      description: 'Improve reading comprehension, grammar, vocabulary, synonyms, antonyms, sentence correction, and para jumbles.',
      suggestedTopics: ['Reading Comprehension', 'Grammar', 'Vocabulary', 'Synonyms', 'Antonyms', 'Sentence Correction']
    },
    'programming': {
      title: 'Programming MCQs Test Setup',
      description: 'Master object-oriented programming, data structures, algorithms, database management, operating systems, and computer networks.',
      suggestedTopics: ['OOPs', 'Data Structures', 'Algorithms', 'DBMS', 'OS', 'Computer Networks']
    },
    'mixed': {
      title: 'Mixed Test Setup',
      description: 'A curated blend of aptitude, logical reasoning, verbal ability, and programming questions with AI-recommended distribution.',
      suggestedTopics: []
    },
    'company': {
      title: 'Company-Specific Test Setup',
      description: 'Tailored mock tests matching the hiring patterns and question styles of specific companies like TCS, Infosys, Wipro, and more.',
      suggestedTopics: []
    },
    'resume': {
      title: 'Resume-Based Test Setup',
      description: 'Upload your resume and get a personalized test based on your skills, experience, and the roles you are targeting.',
      suggestedTopics: []
    }
  };

  private apiCategoryMap: Record<string, string> = {
    'aptitude': 'APTITUDE',
    'logical': 'LOGICAL_REASONING',
    'verbal': 'VERBAL_ABILITY',
    'programming': 'PROGRAMMING'
  };

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private placementService: PlacementService,
    private _toastr: ToastrService,
	private dialog:MatDialog,
  ) { }

  toggleCompanyDropdown(event: Event) {
    event.stopPropagation();
    event.preventDefault();
    this.ignoreNextDocumentClick = true;
    this.isCompanyDropdownOpen = !this.isCompanyDropdownOpen;
    this.isRoleDropdownOpen = false; // Close other dropdown
  }

  toggleRoleDropdown(event: Event) {
    event.stopPropagation();
    event.preventDefault();
    this.ignoreNextDocumentClick = true;
    this.isRoleDropdownOpen = !this.isRoleDropdownOpen;
    this.isCompanyDropdownOpen = false; // Close other dropdown
  }

  closeDropdowns() {
    this.isCompanyDropdownOpen = false;
    this.isRoleDropdownOpen = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    if (this.ignoreNextDocumentClick) {
      this.ignoreNextDocumentClick = false;
      return;
    }
    this.closeDropdowns();
  }

  selectCompany(company: string) {
    this.selectedCompany = company;
    this.companySearch = ''; // Clear search term
    this.isCompanyDropdownOpen = false;
  }

  selectRole(role: string) {
    this.selectedRole = role;
    this.roleSearch = ''; // Clear search term
    this.isRoleDropdownOpen = false;
  }

  get filteredCompanies() {
    return this.companies.filter(c => c.toLowerCase().includes(this.companySearch.toLowerCase()));
  }

  get filteredRoles() {
    return this.roles.filter(r => r.toLowerCase().includes(this.roleSearch.toLowerCase()));
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;
    const file = input.files[0];

    if (file) {

      if (file.size > 5 * 1024 * 1024) {
        this._toastr.error('File size exceeds 5 MB limit');
        input.value = ''; // Reset input
        return;
      }

      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain'];

      const extension = file.name.split('.').pop()?.toLowerCase();
      const allowedExtensions = ['pdf', 'doc', 'docx', 'txt'];

      if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(extension || '')) {
        this._toastr.error('Invalid file type. Only PDF, DOCX, and TXT are supported.');
        input.value = '';
        return;
      }

      this.uploadedFileName = file.name;
      this.uploadedFile = file; // Store file reference
      this.extractSkills(file);
    }
  }

  removeFile() {
    this.uploadedFileName = null;
    this.uploadedFile = null;
    this.extractedSkills = [];
    this.isExtracting = false;
  }

  extractSkills(file: File) {
    this.isExtracting = true;
    this.placementService.extractSkills(file).subscribe({
      next: (res) => {
        this.isExtracting = false;
        if (res.Success && res.Data) {

          if (res.Data.extracted_skills && res.Data.extracted_skills.length > 0) {
            this.extractedSkills = res.Data.extracted_skills;
          } else if (res.Data.skill_categories) {

            this.extractedSkills = Object.values(res.Data.skill_categories)
              .flat()
              .filter((skill): skill is string => typeof skill === 'string');
          }

          if (res.Data.project_experiences) {
            this.extractedExperiences = res.Data.project_experiences;
          }
        }
      },
      error: (err) => {
        this.isExtracting = false;
        console.error('Failed to extract skills:', err);

      }
    });
  }

  removeExtractedSkill(skill: string) {
    this.extractedSkills = this.extractedSkills.filter(s => s !== skill);
  }

  ngOnInit() {
    this.testType = this.route.snapshot.paramMap.get('id') || '';
    if (this.typeConfig[this.testType]) {
      this.config = this.typeConfig[this.testType];

      const apiCategory = this.apiCategoryMap[this.testType];
      if (apiCategory) {
        this.fetchTopics(apiCategory);
      }

      if (this.testType === 'company') {
        this.fetchCompanies();
      }
    } else {

      this.config = {
        title: 'Test Setup',
        description: 'Configure your test settings.',
        suggestedTopics: []
      };
    }
  }

  fetchCompanies() {
    this.placementService.getCompanies().subscribe({
      next: (res) => {
        if (res.Success && res.Data) {

          if (res.Data.companies) {
            this.companies = res.Data.companies;
          }

          if (res.Data.roles) {
            this.roles = res.Data.roles;
          }
        }
      },
      error: (err) => {
        console.error('Failed to fetch companies and roles:', err);
      }
    });
  }

  fetchTopics(category: string) {
    this.placementService.getTopics(category).subscribe({
      next: (res) => {
        if (res.Success && res.Data?.topics) {
          this.config.suggestedTopics = res.Data.topics;
        }
      },
      error: (err) => {
        console.error('Failed to fetch topics:', err);
      }
    });
  }

  goBack() {
    this.router.navigate(['/student-dashboard/placement-test-type']);
  }

  toggleTopic(topic: string) {
    if (this.selectedTopics.includes(topic)) {
      this.selectedTopics = this.selectedTopics.filter(t => t !== topic);
    } else {
      this.selectedTopics.push(topic);
    }
  }

  addTopic() {
    const topic = this.topicInput.trim();
    if (!topic) return;

    if (topic.length > 25) {
      this._toastr.error('Max 25 characters topic is only allowed');
      return;
    }

    if (!this.selectedTopics.includes(topic)) {
      this.selectedTopics.push(topic);
      this.topicInput = '';
    }
  }

  generateTest() {
    if (this.isGeneratingTest) return;

    if (this.testType === 'resume') {
      this.generateResumeBasedTest();
    } else if (this.testType === 'aptitude') {
      this.generateAptitudeTest();
    } else if (this.testType === 'logical') {
      this.generateLogicalTest();
    } else if (this.testType === 'verbal') {
      this.generateVerbalTest();
    } else if (this.testType === 'programming') {
      this.generateProgrammingTest();
    } else if (this.testType === 'mixed') {
      this.generateMixedTest();
    } else if (this.testType === 'company') {
      this.generateCompanySpecificTest();
    } else {

    }
  }

  private generateAptitudeTest() {
    if (this.selectedTopics.length === 0) {
      this._toastr.error('Please select at least one topic');
      return;
    }

    this.isGeneratingTest = true;
    this.placementService.createAptitudeTest(
      this.selectedTopics,
      this.selectedDifficulty,
      this.selectedQuestions,
      false,
      ''
    ).subscribe({
      next: (res:MockTestCreationResponse) => {
        this.isGeneratingTest = false;
        if (res.Success && res.Data) {
          this.router.navigate(['/student-dashboard/placement-test'], {
            state: {
              testId: res.Data.test_id,
              testTitle: 'Aptitude Test',
              questions: res.Data.questions,
              durationMinutes: Math.floor(res.Data.time_limit / 60),
              totalMarks: res.Data.total_marks
            }
          });
		  return;
        }

			if (res.Status === 402 && !res.Success && res.Data) {
				const popupData = {
					title: res.Data?.title,
					message: res.Data?.message
				};

				const dialogRef = this.openAiPlacementLimitDialogBox(popupData);
				this.router.events.subscribe(() => {
					dialogRef.close();
				})
				return;
			}
      },
      error: (err) => {
        this.isGeneratingTest = false;
        console.error('Failed to create aptitude test:', err);
      }
    });
  }

  private generateLogicalTest() {
    if (this.selectedTopics.length === 0) {
      this._toastr.error('Please select at least one topic');
      return;
    }

    this.isGeneratingTest = true;
    this.placementService.createLogicalTest(
      this.selectedTopics,
      this.selectedDifficulty,
      this.selectedQuestions,
      false,
      ''
    ).subscribe({
      next: (res:MockTestCreationResponse) => {
        this.isGeneratingTest = false;
        if (res.Success && res.Data) {
          this.router.navigate(['/student-dashboard/placement-test'], {
            state: {
              testId: res.Data.test_id,
              testTitle: 'Logical Reasoning Test',
              questions: res.Data.questions,
              durationMinutes: Math.floor(res.Data.time_limit / 60),
              totalMarks: res.Data.total_marks
            }
          });
				return;
			}

			if (res.Status === 402 && !res.Success && res.Data) {
				const popupData = {
					title: res.Data?.title,
					message: res.Data?.message
				};

				const dialogRef = this.openAiPlacementLimitDialogBox(popupData);
				this.router.events.subscribe(() => {
					dialogRef.close();
				})
				return;
			}
      },
      error: (err) => {
        this.isGeneratingTest = false;
        console.error('Failed to create logical test:', err);
      }
    });
  }

  private generateVerbalTest() {
    if (this.selectedTopics.length === 0) {
      this._toastr.error('Please select at least one topic');
      return;
    }

    this.isGeneratingTest = true;
    this.placementService.createVerbalTest(
      this.selectedTopics,
      this.selectedDifficulty,
      this.selectedQuestions,
      false,
      ''
    ).subscribe({
      next: (res: MockTestCreationResponse) => {
        this.isGeneratingTest = false;
        if (res.Success && res.Data) {
          this.router.navigate(['/student-dashboard/placement-test'], {
            state: {
              testId: res.Data.test_id,
              testTitle: 'Verbal Ability Test',
              questions: res.Data.questions,
              durationMinutes: Math.floor(res.Data.time_limit / 60),
              totalMarks: res.Data.total_marks
            }
		  });
				return;
			}

			if (res.Status === 402 && !res.Success && res.Data) {
				const popupData = {
					title: res.Data?.title,
					message: res.Data?.message
				};

				const dialogRef = this.openAiPlacementLimitDialogBox(popupData);
				this.router.events.subscribe(() => {
					dialogRef.close();
				})
				return;
			}
      },
      error: (err) => {
        this.isGeneratingTest = false;
        console.error('Failed to create verbal test:', err);
      }
    });
  }

  private generateProgrammingTest() {
    if (this.selectedTopics.length === 0) {
      this._toastr.error('Please select at least one topic');
      return;
    }

    this.isGeneratingTest = true;
    this.placementService.createProgrammingTest(
      this.selectedTopics,
      this.selectedDifficulty,
      this.selectedQuestions,
      false,
      ''
    ).subscribe({
      next: (res: MockTestCreationResponse) => {
        this.isGeneratingTest = false;
        if (res.Success && res.Data) {
          this.router.navigate(['/student-dashboard/placement-test'], {
            state: {
              testId: res.Data.test_id,
              testTitle: 'Programming MCQs Test',
              questions: res.Data.questions,
              durationMinutes: Math.floor(res.Data.time_limit / 60),
              totalMarks: res.Data.total_marks
            }
          });
				return;
			}

			if (res.Status === 402 && !res.Success && res.Data) {
				const popupData = {
					title: res.Data?.title,
					message: res.Data?.message
				};

				const dialogRef = this.openAiPlacementLimitDialogBox(popupData);
				this.router.events.subscribe(() => {
					dialogRef.close();
				})
				return;
			}
      },
      error: (err) => {
        this.isGeneratingTest = false;
        console.error('Failed to create programming test:', err);
      }
    });
  }

  private generateMixedTest() {
    this.isGeneratingTest = true;

    const difficulty = this.selectedDifficulty;
    const numQuestions = Number(this.selectedQuestions);
    const isPublic = false;



    this.placementService.createMixedTest(
      difficulty,
      numQuestions,
      isPublic,
      ''
    ).subscribe({
      next: (res: MockTestCreationResponse) => {
        this.isGeneratingTest = false;
        if (res.Success && res.Data) {
          this.router.navigate(['/student-dashboard/placement-test'], {
            state: {
              testId: res.Data.test_id,
              testTitle: 'Mixed Test',
              questions: res.Data.questions,
              durationMinutes: Math.floor(res.Data.time_limit / 60),
              totalMarks: res.Data.total_marks
            }
          });
				return;
			}

			if (res.Status === 402 && !res.Success && res.Data) {
				const popupData = {
					title: res.Data?.title,
					message: res.Data?.message
				};

				const dialogRef = this.openAiPlacementLimitDialogBox(popupData);
				this.router.events.subscribe(() => {
					dialogRef.close();
				})
				return;
			}
      },
      error: (err) => {
        this.isGeneratingTest = false;
        console.error('Failed to create mixed test:', err);
        this._toastr.error('Failed to generate test. Please try again.');
      }
    });
  }

  private generateCompanySpecificTest() {
    if (!this.selectedCompany) {
      this._toastr.error('Please select a company');
      return;
    }
    if (!this.selectedRole) {
      this._toastr.error('Please select a role');
      return;
    }

    this.isGeneratingTest = true;
    this.placementService.createCompanySpecificTest(
      this.selectedCompany,
      this.selectedRole,
      this.selectedDifficulty,
      this.selectedQuestions,
      false,
      ''
    ).subscribe({
      next: (res: MockTestCreationResponse) => {
        this.isGeneratingTest = false;
        if (res.Success && res.Data) {
          this.router.navigate(['/student-dashboard/placement-test'], {
            state: {
              testId: res.Data.test_id,
              testTitle: `${this.selectedCompany} - ${this.selectedRole} Test`,
              questions: res.Data.questions,
              durationMinutes: Math.floor(res.Data.time_limit / 60),
              totalMarks: res.Data.total_marks
            }
          });
				return;
			}

			if (res.Status === 402 && !res.Success && res.Data) {
				const popupData = {
					title: res.Data?.title,
					message: res.Data?.message
				};

				const dialogRef = this.openAiPlacementLimitDialogBox(popupData);
				this.router.events.subscribe(() => {
					dialogRef.close();
				})
				return;
			}

      },
      error: (err) => {
        this.isGeneratingTest = false;
        console.error('Failed to create company-specific test:', err);
      }
    });
  }

  private generateResumeBasedTest() {
    if (!this.uploadedFile) {
      console.error('No file uploaded');
      return;
    }

    if (this.isExtracting) {
      this._toastr.info('Please wait till we extract the skills from your resume');
      return;
    }

    this.isGeneratingTest = true;



    this.placementService.createResumeBasedTestFromData(
      this.extractedSkills,
      this.extractedExperiences,
      this.selectedDifficulty,
      this.selectedQuestions,
      false,
      ''
    ).subscribe({
      next: (res:MockTestCreationResponse) => {
        this.isGeneratingTest = false;
        if (res.Success && res.Data) {

          this.router.navigate(['/student-dashboard/placement-test'], {
            state: {
              testId: res.Data.test_id,
              testTitle: 'Resume-Based Test',
              questions: res.Data.questions,
              durationMinutes: Math.floor(res.Data.time_limit / 60),
              totalMarks: res.Data.total_marks
            }
          });
				return;
			}

			if (res.Status === 402 && !res.Success && res.Data) {
				const popupData = {
					title: res.Data?.title,
					message: res.Data?.message
				};

				const dialogRef = this.openAiPlacementLimitDialogBox(popupData);
				this.router.events.subscribe(() => {
					dialogRef.close();
				})
				return;
			}
      },
      error: (err) => {
        this.isGeneratingTest = false;
        console.error('Failed to create test:', err);

      }
    });
  }

	openAiPlacementLimitDialogBox(data: { title: string, message: string }): MatDialogRef<EnrollCoursesDialogBoxComponent> {
		return this.dialog.open(EnrollCoursesDialogBoxComponent, {
			data
		})
	}

}
